import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  checkCJConfirmation, clearCJConfirmation, confirmCJPassword, getCJConfirmationState,
  revokeCJConfirmation, subscribeCJConfirmation,
} from "./CJPasswordConfirmationSession";

const CJPasswordConfirmationContext = createContext(null);

export function CJPasswordConfirmationProvider({ children }) {
  const [session, setSession] = useState(getCJConfirmationState);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [pendingProtectedAction, setPendingProtectedAction] = useState(null);

  useEffect(() => subscribeCJConfirmation(setSession), []);
  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((new Date(session.expiresAt || 0).getTime() - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (session.confirmationToken && remaining === 0) clearCJConfirmation();
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [session.expiresAt]);
  // Fire-and-forget: checkCJConfirmation() itself never rejects (see its
  // own comment for the "Failed to fetch" bug this used to cause), but the
  // .catch() stays as defense in depth for a fire-and-forget async call
  // with no await/error handling here otherwise.
  useEffect(() => { if (session.confirmationToken) checkCJConfirmation().catch(() => {}); }, [session.confirmationToken]);

  const requireConfirmation = useCallback(async ({ action, onConfirmed }) => {
    if (session.confirmationToken && await checkCJConfirmation()) return onConfirmed?.(getCJConfirmationState().confirmationToken);
    setPendingProtectedAction({ action, onConfirmed });
    return null;
  }, [session.confirmationToken]);
  const resumePending = useCallback(async (password) => {
    const confirmation = await confirmCJPassword(password);
    const pending = pendingProtectedAction;
    setPendingProtectedAction(null);
    await pending?.onConfirmed?.(confirmation.confirmationToken);
    return confirmation;
  }, [pendingProtectedAction]);
  const value = useMemo(() => ({
    isConfirmed: Boolean(session.confirmationToken && remainingSeconds > 0), confirmationToken: session.confirmationToken,
    expiresAt: session.expiresAt, remainingSeconds, loading: session.loading, error: session.error,
    confirmPassword: confirmCJPassword, requireConfirmation, refreshActivity: checkCJConfirmation,
    revokeConfirmation: revokeCJConfirmation, clearConfirmation: clearCJConfirmation,
    pendingProtectedAction, resumePending,
  }), [session, remainingSeconds, requireConfirmation, pendingProtectedAction, resumePending]);
  return <CJPasswordConfirmationContext.Provider value={value}>{children}</CJPasswordConfirmationContext.Provider>;
}

export const useCJPasswordConfirmation = () => {
  const value = useContext(CJPasswordConfirmationContext);
  if (!value) throw new Error("useCJPasswordConfirmation must be used inside CJPasswordConfirmationProvider");
  return value;
};
